import React, {
  useContext, useState, useMemo,
} from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  List,
  SvgIcon,
  IconButton,
} from '@mui/material';
import Cross from '@digital-hig/icon/lib/build/icons/utility/cross';
import Typography from '@digital-hig/typography';
import SvgCaretDown from '@digital-hig/icon/lib/build/ui-controls/caret-down';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import SRPStateInterface from '../../../common/context/srpInterface';
import { SRPContext } from '../../../common/context/srpContext';
import { PARTNERS_FILTER_TYPE } from '../../../constants/app-constants';
import FilterRootElement from './style';
import FilterListItem from './FilterListItem';

const Filters = ({ handleClose }) => {
  const {
    srpStateRef = { current: SRPStateInterface },
    filters,
    appliedFilter,
    fetchPartnersData,
    dispatch,
    setAppliedFilter,
  } = useContext(SRPContext);
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState({
    offerings: true,
    locations: false,
    languages: false,
    solutions: false,
    services: false,
    specializations: false,
    affiliations: false,
  });
  const onchangeInput = async (option, name) => {
    dispatch({ type: 'GET_SRP_LISTING', payload: [] });
    const applyData = srpStateRef?.current?.appliedFilter || appliedFilter;
    let uniqueByValue = '';
    if (name === 'solutions') {
      uniqueByValue = option.filter((item, index, self) => index === self.findIndex((r) => r.value === item.value));
    } else {
      uniqueByValue = option;
    }
    const search = { ...applyData, pageNumber: 1, [name]: uniqueByValue };
    await setAppliedFilter(search);
    await fetchPartnersData({ search });
  };

  const handleAccordionChange = (key) => {
    setExpanded((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };
  const handleInputChange = (option, filterKey, isSelected) => {
    const selectedValue = appliedFilter[filterKey];
    const updatedValue = isSelected
      ? selectedValue.filter((item) => item.value !== option.value)
      : [...selectedValue, option];
    onchangeInput(updatedValue, filterKey);
  };

  // Memoize and sort filters based on PARTNERS_FILTER_TYPE
  const sortedFilters = useMemo(() => Object.keys(filters)
    .sort((a, b) => Object.values(PARTNERS_FILTER_TYPE).indexOf(a) - Object.values(PARTNERS_FILTER_TYPE).indexOf(b))
    .reduce((acc, key) => ({ ...acc, [key]: filters[key] }), // Return a new object to avoid mutating the accumulator
      {}), [filters]);

  const getFilterDisplayName = (key) => {
    let filterKey = Object.keys(PARTNERS_FILTER_TYPE).find((filter) => filter.toLowerCase() === key);
    if (filterKey === 'offerings') {
      filterKey = 'I’m interested in';
    }
    return filterKey ? t(filterKey) : filterKey;
  };

  return (
    <FilterRootElement>
      <Typography
        className="filter-heading-mobile"
        data-testid="Filters"
        variant="headline-smaller"
      >
        {t('Filters')}
        <IconButton variant="text" onClick={() => handleClose()} data-testid="close-button">
          <SvgIcon fontSize="large">
            <Cross />
          </SvgIcon>
        </IconButton>
      </Typography>
      {Object.keys(sortedFilters).map(
        (filterKey) => Object.values(PARTNERS_FILTER_TYPE).includes(filterKey) && (
          <Accordion
            className="accordion-list"
            key={filterKey}
            expanded={expanded[filterKey]}
            onChange={() => handleAccordionChange(filterKey)}
          >
            <AccordionSummary
              data-testid={filterKey}
              className="accordion-heading"
            >
              <Typography variant="headline-smaller" data-testid={filterKey} data-wat-val={filterKey} data-wat-loc="filters category">
                {getFilterDisplayName(filterKey)}
                {appliedFilter[filterKey] && appliedFilter[filterKey].length ? ` (${appliedFilter[filterKey].length})` : null}
              </Typography>
              <SvgCaretDown width="12px" />
            </AccordionSummary>
            <AccordionDetails className="accordion-content">
              <FormGroup>
                {sortedFilters[filterKey].map((element) => {
                  const isSelected = appliedFilter[filterKey]?.some(
                    (item) => item.value === element.value,
                  );
                  // Render group if it exists
                  if (element.group) {
                    return (
                      <Accordion key={element.group} className="sub-accordion-block">
                        <AccordionSummary
                          data-testid={element.group}
                          className="accordion-heading sub-accordion-heading"
                        >
                          <SvgCaretDown width="12px" />
                          <Typography data-testid={element.group} title={t(element.group)} data-wat-val={element.group} data-wat-loc="filters group">
                            {t(element.group)}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails className="accordion-group-detail">
                          <List>
                            {element.values.map((option) => {
                              const isOptionSelected = appliedFilter[filterKey]?.some((item) => item.value === option.value) || false;
                              return (
                                <FilterListItem
                                  key={`checkbox-${option.value}`}
                                  filterKey={filterKey}
                                  option={option}
                                  isSelected={isOptionSelected}
                                  handleInputChange={handleInputChange}
                                />
                              );
                            })}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    );
                  }
                  // Render non-grouped option
                  return (
                    <FilterListItem
                      key={`checkbox-${element.value}`}
                      option={element}
                      isSelected={isSelected}
                      filterKey={filterKey}
                      handleInputChange={handleInputChange}
                    />
                  );
                })}
              </FormGroup>
            </AccordionDetails>
          </Accordion>
        ),
      )}
    </FilterRootElement>
  );
};
Filters.propTypes = {
  handleClose: PropTypes.func,
};
Filters.defaultProps = {
  handleClose: () => {},
};
export default Filters;
